import api from "../http/api";

/**
 * Busca todas as preferências do usuário autenticado
 */
export const getPreferences = async () => {
  try {
    const response = await api.client.get("api/v1/Preferences");
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return [];
    }
    console.error("Erro ao buscar preferências:", error);
    throw error;
  }
};

/**
 * Busca uma preferência específica pelo nome da chave
 */
export const getPreferenceByKey = async (preferenceKey) => {
  try {
    const preferences = await getPreferences();
    return preferences.find(p => p.preferenceKey === preferenceKey) || null;
  } catch (error) {
    console.error("Erro ao buscar preferência:", error);
    return null;
  }
};

/**
 * Cria uma nova preferência
 * A API usa o usuário autenticado via token JWT, não precisa enviar userId/institutionId
 */
export const createPreference = async ({ preferenceKey, preferenceValue }) => {
  try {
    const payload = {
      preferenceKey,
      preferenceValue
    };

    const response = await api.client.post("api/v1/Preferences", payload);
    return response.data;
  } catch (error) {
    console.error("Erro ao criar preferência:", error);
    throw error;
  }
};

/**
 * Atualiza uma preferência existente
 */
export const updatePreference = async ({ preferenceKey, preferenceValue }) => {
  try {
    const payload = {
      preferenceKey,
      preferenceValue
    };

    const response = await api.client.put("api/v1/Preferences", payload);
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar preferência:", error);
    throw error;
  }
};

/**
 * Deleta uma preferência
 */
export const deletePreference = async (preferenceKey) => {
  try {
    const response = await api.client.delete(`api/v1/Preferences?preferenceKey=${preferenceKey}`);
    return response.data;
  } catch (error) {
    console.error("Erro ao deletar preferência:", error);
    throw error;
  }
};

// Constantes para as chaves de preferência
export const PREFERENCE_KEYS = {
  DASHBOARD_FAVORITO: "DashboardFavorito",
  PRIMARY_COLOR: "PrimaryColor",
  SECONDARY_COLOR: "SecondaryColor",
  INSTITUTION_LOGO: "InstitutionLogo"
};

// Valores padrão do sistema para o tema
export const DEFAULT_THEME = {
  primaryColor: "#0962ad",
  secondaryColor: "#3a8fd8",
  logo: null
};

/**
 * Busca o dashboard favorito do usuário
 */
export const getFavoriteDashboard = async () => {
  try {
    const preference = await getPreferenceByKey(PREFERENCE_KEYS.DASHBOARD_FAVORITO);
    return preference?.preferenceValue || null;
  } catch (error) {
    console.error("Erro ao buscar dashboard favorito:", error);
    return null;
  }
};

/**
 * Define um dashboard como favorito (substitui o anterior se existir)
 */
export const setFavoriteDashboard = async (dashboardId) => {
  try {
    const existingPreference = await getPreferenceByKey(PREFERENCE_KEYS.DASHBOARD_FAVORITO);
    
    if (existingPreference) {
      // Atualiza a preferência existente
      return await updatePreference({
        preferenceKey: PREFERENCE_KEYS.DASHBOARD_FAVORITO,
        preferenceValue: String(dashboardId)
      });
    } else {
      // Cria uma nova preferência
      return await createPreference({
        preferenceKey: PREFERENCE_KEYS.DASHBOARD_FAVORITO,
        preferenceValue: String(dashboardId)
      });
    }
  } catch (error) {
    console.error("Erro ao definir dashboard favorito:", error);
    throw error;
  }
};

/**
 * Remove o dashboard favorito
 */
export const removeFavoriteDashboard = async () => {
  try {
    return await deletePreference(PREFERENCE_KEYS.DASHBOARD_FAVORITO);
  } catch (error) {
    console.error("Erro ao remover dashboard favorito:", error);
    throw error;
  }
};

/**
 * Busca o tema da instituição (cores e logo)
 * Retorna os valores padrão se não existirem preferências
 */
export const getInstitutionTheme = async () => {
  try {
    const preferences = await getPreferences();
    
    const primaryColorPref = preferences.find(p => p.preferenceKey === PREFERENCE_KEYS.PRIMARY_COLOR);
    const secondaryColorPref = preferences.find(p => p.preferenceKey === PREFERENCE_KEYS.SECONDARY_COLOR);
    const logoPref = preferences.find(p => p.preferenceKey === PREFERENCE_KEYS.INSTITUTION_LOGO);
    
    return {
      primaryColor: primaryColorPref?.preferenceValue || DEFAULT_THEME.primaryColor,
      secondaryColor: secondaryColorPref?.preferenceValue || DEFAULT_THEME.secondaryColor,
      logo: logoPref?.preferenceValue || DEFAULT_THEME.logo
    };
  } catch (error) {
    console.error("Erro ao buscar tema da instituição:", error);
    return DEFAULT_THEME;
  }
};

/**
 * Salva ou atualiza uma preferência de tema específica
 */
const saveThemePreference = async (preferenceKey, preferenceValue) => {
  try {
    const existingPreference = await getPreferenceByKey(preferenceKey);
    
    if (existingPreference) {
      return await updatePreference({ preferenceKey, preferenceValue });
    } else {
      return await createPreference({ preferenceKey, preferenceValue });
    }
  } catch (error) {
    console.error(`Erro ao salvar preferência ${preferenceKey}:`, error);
    throw error;
  }
};

/**
 * Verifica se um valor de preferência é válido para ser enviado
 * Não envia se for null, undefined, string vazia ou igual ao valor padrão
 */
const isValidPreferenceValue = (value, defaultValue) => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  // Se for igual ao valor padrão, não precisa salvar
  if (value === defaultValue) {
    return false;
  }
  return true;
};

/**
 * Salva o tema da instituição (apenas cores)
 * O logo é enviado separadamente via multipart/form-data usando saveInstitutionLogo
 * @param {Object} themeData - Objeto contendo primaryColor e/ou secondaryColor
 */
export const setInstitutionTheme = async (themeData) => {
  try {
    const promises = [];
    
    // Só salva cor primária se foi preenchida e é diferente do padrão
    if (isValidPreferenceValue(themeData.primaryColor, DEFAULT_THEME.primaryColor)) {
      promises.push(saveThemePreference(PREFERENCE_KEYS.PRIMARY_COLOR, themeData.primaryColor));
    }
    
    // Só salva cor secundária se foi preenchida e é diferente do padrão
    if (isValidPreferenceValue(themeData.secondaryColor, DEFAULT_THEME.secondaryColor)) {
      promises.push(saveThemePreference(PREFERENCE_KEYS.SECONDARY_COLOR, themeData.secondaryColor));
    }
    
    // Só executa se houver algo para salvar
    if (promises.length > 0) {
      await Promise.all(promises);
    }
    
    return true;
  } catch (error) {
    console.error("Erro ao salvar tema da instituição:", error);
    throw error;
  }
};

/**
 * Remove uma preferência de tema específica (volta ao padrão)
 */
export const removeThemePreference = async (preferenceKey) => {
  try {
    return await deletePreference(preferenceKey);
  } catch (error) {
    console.error(`Erro ao remover preferência ${preferenceKey}:`, error);
    throw error;
  }
};

/**
 * Reseta o tema da instituição para os valores padrão
 */
export const resetInstitutionTheme = async () => {
  try {
    await Promise.all([
      deletePreference(PREFERENCE_KEYS.PRIMARY_COLOR).catch(() => {}),
      deletePreference(PREFERENCE_KEYS.SECONDARY_COLOR).catch(() => {}),
      deletePreference(PREFERENCE_KEYS.INSTITUTION_LOGO).catch(() => {})
    ]);
    return true;
  } catch (error) {
    console.error("Erro ao resetar tema da instituição:", error);
    throw error;
  }
};

/**
 * Faz upload do logo da instituição (POST - criar novo)
 * @param {File} file - Arquivo de imagem do logo
 * @param {string} institutionId - ID da instituição
 */
export const uploadInstitutionLogo = async (file, institutionId) => {
  try {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("InstitutionId", institutionId);

    const response = await api.client.post("api/v1/preferences/logo", formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    
    return response.data;
  } catch (error) {
    console.error("Erro ao fazer upload do logo:", error);
    throw error;
  }
};

/**
 * Atualiza o logo da instituição (PUT - atualizar existente)
 * @param {File} file - Arquivo de imagem do logo
 * @param {string} institutionId - ID da instituição
 */
export const updateInstitutionLogo = async (file, institutionId) => {
  try {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("InstitutionId", institutionId);

    const response = await api.client.put("api/v1/preferences/logo", formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar logo:", error);
    throw error;
  }
};

/**
 * Salva ou atualiza o logo da instituição
 * Verifica se já existe um logo e decide entre POST ou PUT
 * @param {File} file - Arquivo de imagem do logo
 * @param {string} institutionId - ID da instituição
 */
export const saveInstitutionLogo = async (file, institutionId) => {
  try {
    // Verifica se já existe um logo
    const existingLogo = await getPreferenceByKey(PREFERENCE_KEYS.INSTITUTION_LOGO);
    
    if (existingLogo) {
      return await updateInstitutionLogo(file, institutionId);
    } else {
      return await uploadInstitutionLogo(file, institutionId);
    }
  } catch (error) {
    console.error("Erro ao salvar logo:", error);
    throw error;
  }
};

/**
 * Remove o logo da instituição
 */
export const removeInstitutionLogo = async () => {
  try {
    return await deletePreference(PREFERENCE_KEYS.INSTITUTION_LOGO);
  } catch (error) {
    console.error("Erro ao remover logo:", error);
    throw error;
  }
};
